import React from 'react';
import { styled } from '@mui/material/styles';

// Gauge arc: 270° sweep, gap at the bottom, flat-cut ends — same data contract as the old full ring.
const RADIUS = 30;
const STROKE = 13;
const CENTER = 42;
const BOX_W = 84;
const BOX_H = 72;
const SWEEP_FRACTION = 0.75;
const ARC_LENGTH = 2 * Math.PI * RADIUS * SWEEP_FRACTION;
// 270° arc from bottom-left (135°) clockwise over the top to bottom-right (45°).
const ARC_X = RADIUS * Math.SQRT1_2;
const ARC_START = { x: CENTER - ARC_X, y: CENTER + ARC_X };
const ARC_END = { x: CENTER + ARC_X, y: CENTER + ARC_X };
const ARC_PATH = `M ${ARC_START.x.toFixed(2)} ${ARC_START.y.toFixed(2)} A ${RADIUS} ${RADIUS} 0 1 1 ${ARC_END.x.toFixed(2)} ${ARC_END.y.toFixed(2)}`;

const RingWrap = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
});

const RingBox = styled('div')({
  position: 'relative',
  width: `${BOX_W}px`,
  height: `${BOX_H}px`,
});

// Padding shifts the flex center down onto the arc's center so the numbers sit dead-center in the hole.
const CenterLabel = styled('div')({
  position: 'absolute',
  inset: 0,
  boxSizing: 'border-box',
  paddingTop: `${(CENTER * 2 - BOX_H)}px`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const DoneCount = styled('span')({
  fontSize: '1.3rem',
  fontWeight: 700,
  color: '#424242',
  letterSpacing: '-0.5px',
  lineHeight: 1,
});

const OfTotal = styled('span')({
  fontSize: '0.62rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.5)',
  marginTop: '2px',
});

const RingTitle = styled('span')({
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.6)',
  whiteSpace: 'nowrap',
});

export interface ProgressRingProps {
  title: string;
  done: number;
  total: number;
  color: string;
  trackColor: string;
}

export default function ProgressRing({ title, done, total, color, trackColor }: ProgressRingProps): JSX.Element {
  const fraction = total > 0 ? done / total : 0;
  const offset = (ARC_LENGTH * (1 - fraction)).toFixed(2);
  return (
    <RingWrap>
      <RingTitle>{title}</RingTitle>
      <RingBox>
        <svg width={BOX_W} height={BOX_H} viewBox={`0 0 ${BOX_W} ${BOX_H}`} role='img' aria-label={`${title}: ${done} of ${total}`}>
          <path d={ARC_PATH} fill='none' strokeWidth={STROKE} stroke={trackColor} />
          <path
            d={ARC_PATH}
            fill='none'
            strokeWidth={STROKE}
            stroke={color}
            strokeDasharray={ARC_LENGTH}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.4s ease' }}
          />
        </svg>
        <CenterLabel>
          <DoneCount>{done}</DoneCount>
          <OfTotal>of {total}</OfTotal>
        </CenterLabel>
      </RingBox>
    </RingWrap>
  );
}
